/**
 * KB assistant — scan results, industry correction, guided questions, conversation goal.
 * Collects answers in state only. Does not write the knowledge base.
 * Mounted on the Knowledge Base tab and the setup wizard.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { OVERRIDABLE_SETTINGS } from '../../kb-assistant/compliancePolicy';
import { COVERAGE_TOPICS } from '../../kb-assistant/coverageScan';
import {
  CONVERSATION_GOAL_ID,
  CONVERSATION_GOALS,
  INDUSTRY_IDS,
  INDUSTRY_LABELS,
  REGULATED_VERTICAL_CONFIRM_ID,
  REGULATED_VERTICAL_CONFIRM_OPTIONS,
} from '../../kb-assistant/industryPacks';
import { appendDraftBlock, buildAppendDiff, formatDraftAdditions } from '../../kb-assistant/applyDraft';
import { runKbAssistant } from '../../kb-assistant/runKbAssistant';
import { draftKbAssistant } from '../../utils/api';

const TOPIC_COPY = {
  pricing: { found: 'Pricing', missing: 'Pricing' },
  services: { found: 'Services or products', missing: 'Services or products' },
  serviceArea: { found: 'Service area', missing: 'Service area' },
  hours: { found: 'Hours and response time', missing: 'Hours and response time' },
  contactRoutes: { found: 'How to reach you', missing: 'How to reach you' },
  disqualifiers: { found: 'What you do not do', missing: 'What you do not do' },
  differentiators: { found: 'What makes you different', missing: 'What makes you different' },
};

function buildPayload({
  answers,
  conversationGoal,
  goalCustom,
  goalQualifiers,
  selection,
  chosenIndustry,
  otherBusinessDescription,
}) {
  return {
    answers,
    conversationGoal: conversationGoal || '',
    goalCustom,
    goalQualifiers,
    industry: chosenIndustry || selection.industry,
    industryWasChosen: Boolean(chosenIndustry),
    detection: selection.detection,
    otherBusinessDescription: chosenIndustry === 'generic' ? otherBusinessDescription || '' : '',
  };
}

function WhyToggle({ questionId, why }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ margin: '0.3rem 0 0.65rem' }}>
      <button
        type="button"
        className="fb-btn-ghost-sm"
        data-testid={`kb-assistant-why-toggle-${questionId}`}
        onClick={() => setOpen((v) => !v)}
        style={{ padding: '0.15rem 0', fontSize: '0.82rem' }}
      >
        {open ? 'Hide why this matters' : 'Why this matters'}
      </button>
      {open ? (
        <p
          className="fb-muted"
          data-testid={`kb-assistant-why-${questionId}`}
          style={{ fontSize: '0.85rem', margin: '0.35rem 0 0' }}
        >
          {why}
        </p>
      ) : null}
    </div>
  );
}

export default function KbAssistantPanel({
  kbContent,
  scrapedPageUrls = [],
  discoveredLinks,
  onStateChange,
  variant = 'tab',
  workspaceId,
  onApplyDraft,
  onHandoffTestQuestion,
}) {
  const [chosenIndustry, setChosenIndustry] = useState('');
  const [otherBusinessDescription, setOtherBusinessDescription] = useState('');
  const [answers, setAnswers] = useState({});
  const [conversationGoal, setConversationGoal] = useState('');
  const [goalCustom, setGoalCustom] = useState('');
  const [goalQualifiers, setGoalQualifiers] = useState('');
  const [step, setStep] = useState(0);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [draftAdditions, setDraftAdditions] = useState('');
  const [testQuestion, setTestQuestion] = useState('');
  const [previousContent, setPreviousContent] = useState('');
  const [draftApplied, setDraftApplied] = useState(false);

  const scrapedKey = (scrapedPageUrls || []).join('\n');
  const discoveredKey = (discoveredLinks || []).join('\n');

  const { coverage, selection } = useMemo(
    () =>
      runKbAssistant({
        kbContent,
        scrapedPageUrls: scrapedKey ? scrapedKey.split('\n') : [],
        discoveredLinks: discoveredKey ? discoveredKey.split('\n') : undefined,
        chosenIndustry: chosenIndustry || undefined,
        regulatedConfirm: answers[REGULATED_VERTICAL_CONFIRM_ID] || undefined,
      }),
    [kbContent, scrapedKey, discoveredKey, chosenIndustry, answers]
  );

  useEffect(() => {
    if (typeof onStateChange !== 'function') return;
    onStateChange(
      buildPayload({
        answers,
        conversationGoal,
        goalCustom,
        goalQualifiers,
        selection,
        chosenIndustry,
        otherBusinessDescription,
      })
    );
  }, [
    answers,
    conversationGoal,
    goalCustom,
    goalQualifiers,
    selection,
    chosenIndustry,
    otherBusinessDescription,
    onStateChange,
  ]);

  const pickerFirst = selection.requireIndustryPick;
  const questions = pickerFirst ? [] : selection.questions.filter((q) => q.kind !== 'hardRule');
  const detection = selection.detection;
  const hardRules = selection.hardRules || [];
  const pricingPresent = coverage.coverage.pricing.status === 'present';
  const scrapeListGaps = coverage.missingHighValuePages.filter(() => pricingPresent);
  const pagesToAdd = coverage.missingHighValuePages.filter(() => !pricingPresent);
  const total = questions.length;
  const currentIndex = total === 0 ? 0 : Math.min(step, total - 1);
  const current = questions[currentIndex];
  const answeredCount = questions.filter((q) => {
    if (q.id === CONVERSATION_GOAL_ID) return Boolean(conversationGoal);
    return Boolean((answers[q.id] || '').trim());
  }).length;

  function setAnswer(id, value) {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }

  function handleGoalChange(next) {
    setConversationGoal(next);
    const label = CONVERSATION_GOALS.find((g) => g.id === next)?.label || next;
    const text = next === 'custom' ? goalCustom : label;
    setAnswer(CONVERSATION_GOAL_ID, text);
  }

  function handleGoalCustomChange(value) {
    setGoalCustom(value);
    if (conversationGoal === 'custom') {
      setAnswer(CONVERSATION_GOAL_ID, value);
    }
  }

  function handleIndustryChange(value) {
    setChosenIndustry(value);
    setStep(0);
  }

  const canDraft = Boolean(workspaceId && typeof onApplyDraft === 'function');
  const reviewOpen = Boolean(draftAdditions) && !draftApplied;
  const diffLines = reviewOpen ? buildAppendDiff(draftAdditions) : [];

  async function handleDraft() {
    if (!canDraft || drafting) return;
    setDrafting(true);
    setDraftError('');
    setDraftApplied(false);
    try {
      const result = await draftKbAssistant({
        workspaceId,
        kbContent,
        coverage,
        answers,
        conversationGoal: conversationGoal === 'custom' ? goalCustom : conversationGoal,
        goalQualifiers,
        industryLabel: selection.industryLabel,
        otherBusinessDescription:
          chosenIndustry === 'generic' ? otherBusinessDescription.trim() : '',
      });
      const additions = formatDraftAdditions(result.sections || []);
      if (!additions) {
        throw new Error('The draft came back empty. Add an answer and try again.');
      }
      setDraftAdditions(additions);
      setTestQuestion(result.testQuestion || '');
    } catch (error) {
      setDraftError(error?.message || 'Draft failed');
    } finally {
      setDrafting(false);
    }
  }

  async function handleApprove() {
    if (!canDraft || !draftAdditions.trim()) return;
    const previous = kbContent;
    const next = appendDraftBlock(kbContent, draftAdditions);
    try {
      await onApplyDraft(next);
      setPreviousContent(previous);
      setDraftApplied(true);
      if (typeof onHandoffTestQuestion === 'function' && testQuestion) {
        onHandoffTestQuestion(testQuestion);
      }
    } catch (error) {
      setDraftError(error?.message || 'Could not save the knowledge base');
    }
  }

  function handleReject() {
    setDraftAdditions('');
    setTestQuestion('');
    setDraftError('');
    setDraftApplied(false);
  }

  async function handleRestore() {
    if (!canDraft || !previousContent) return;
    await onApplyDraft(previousContent);
    setPreviousContent('');
    setDraftApplied(false);
    setDraftAdditions('');
  }

  function renderGoalFields() {
    return (
      <div data-testid="kb-assistant-goal">
        <select
          id="kb-assistant-goal-select"
          data-testid="kb-assistant-goal-select"
          className="fb-select"
          value={conversationGoal}
          onChange={(e) => handleGoalChange(e.target.value)}
        >
          <option value="">Choose a goal</option>
          {CONVERSATION_GOALS.map((goal) => (
            <option key={goal.id} value={goal.id}>
              {goal.label}
            </option>
          ))}
        </select>
        {conversationGoal === 'custom' && (
          <input
            type="text"
            className="fb-input"
            data-testid="kb-assistant-goal-custom"
            value={goalCustom}
            onChange={(e) => handleGoalCustomChange(e.target.value)}
            placeholder="Describe the goal in your words"
            style={{ marginTop: '0.75rem' }}
          />
        )}
        <label className="fb-label" htmlFor="kb-assistant-goal-qualifiers" style={{ marginTop: '1rem' }}>
          What must be true first? (optional)
        </label>
        <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0.35rem 0 0.5rem' }}>
          Budget, service area, timeline, decision-maker — the AI should check these before pushing for the goal.
        </p>
        <textarea
          id="kb-assistant-goal-qualifiers"
          data-testid="kb-assistant-goal-qualifiers"
          className="fb-textarea"
          rows={2}
          value={goalQualifiers}
          onChange={(e) => setGoalQualifiers(e.target.value)}
          placeholder="e.g. In our service area, budget above $450/mo"
        />
      </div>
    );
  }

  return (
    <div
      className={variant === 'wizard' ? undefined : 'fb-card'}
      data-testid="kb-assistant-panel"
      style={variant === 'wizard' ? { marginBottom: '1.5rem' } : { marginBottom: '2rem' }}
    >
      <h3 className={variant === 'wizard' ? undefined : 'fb-subsection-title'} style={{ marginTop: 0 }}>
        What the AI still needs
      </h3>
      <p className="fb-muted" style={{ marginBottom: '1.25rem', fontSize: '0.9rem' }}>
        We scanned your knowledge base. Present means we found it and can cite it. Missing means the AI
        does not know — and must not guess. Answer what you can. Every answer makes the operator
        better, and you can come back. Nothing is added to the knowledge base until you approve a draft.
      </p>

      <section data-testid="kb-assistant-industry" style={{ marginBottom: '1.5rem' }}>
        <label className="fb-label" htmlFor="kb-assistant-industry-select">
          Industry
        </label>
        {pickerFirst ? (
          <p
            data-testid="kb-assistant-industry-picker-first"
            style={{ fontSize: '0.95rem', margin: '0.35rem 0 0.75rem', color: 'var(--ink, #374151)', lineHeight: 1.5 }}
          >
            We could not confidently tell what kind of business this is. Pick one so we ask the right
            questions.
          </p>
        ) : detection.fallback ? (
          <p className="fb-muted" style={{ fontSize: '0.9rem', margin: '0.35rem 0 0.75rem' }}>
            {detection.evidence[0]?.snippet || 'Industry is unknown. Choose the closest match.'}
          </p>
        ) : (
          <p style={{ fontSize: '0.9rem', margin: '0.35rem 0 0.75rem', color: 'var(--ink, #374151)', lineHeight: 1.5 }}>
            We think this is <strong>{detection.label}</strong>
            {detection.evidence[0] ? (
              <>
                {' '}
                because we found &ldquo;{detection.evidence[0].keyword}&rdquo;
                {detection.evidence.length > 1
                  ? ` and ${detection.evidence.length - 1} more signal${detection.evidence.length > 2 ? 's' : ''}`
                  : ''}
                .
              </>
            ) : (
              '.'
            )}{' '}
            Correct it if we missed.
          </p>
        )}
        {detection.evidence.filter((e) => e.keyword !== 'none').length > 0 && (
          <ul className="fb-muted" style={{ fontSize: '0.82rem', margin: '0 0 0.75rem', paddingLeft: '1.2rem' }}>
            {detection.evidence
              .filter((e) => e.keyword !== 'none')
              .map((e) => (
                <li key={`${e.keyword}:${e.snippet}`}>{e.snippet}</li>
              ))}
          </ul>
        )}
        <select
          id="kb-assistant-industry-select"
          data-testid="kb-assistant-industry-select"
          className="fb-select"
          value={chosenIndustry || (pickerFirst ? '' : selection.industry)}
          onChange={(e) => handleIndustryChange(e.target.value)}
        >
          {pickerFirst ? (
            <option value="" disabled>
              Pick an industry
            </option>
          ) : null}
          {INDUSTRY_IDS.map((id) => (
            <option key={id} value={id}>
              {INDUSTRY_LABELS[id]}
            </option>
          ))}
        </select>
        {chosenIndustry === 'generic' ? (
          <div data-testid="kb-assistant-other-description" style={{ marginTop: '0.85rem' }}>
            <label className="fb-label" htmlFor="kb-assistant-other-description">
              Tell us what you do
            </label>
            <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0.35rem 0 0.5rem' }}>
              A short description is enough — wedding venue, tutoring studio, donor-funded nonprofit.
              Generic questions still apply; this just tells the draft what kind of business it is.
            </p>
            <textarea
              id="kb-assistant-other-description"
              data-testid="kb-assistant-other-description-input"
              className="fb-textarea"
              rows={2}
              maxLength={400}
              value={otherBusinessDescription}
              onChange={(e) => setOtherBusinessDescription(e.target.value)}
              placeholder="e.g. wedding venue"
            />
          </div>
        ) : null}
        {selection.support === 'restricted' && selection.restrictedNotice ? (
          <div className="fb-callout info" data-testid="kb-assistant-restricted" style={{ marginTop: '0.85rem' }}>
            {selection.restrictedNotice}
          </div>
        ) : null}
      </section>

      <section data-testid="kb-assistant-hard-rules" style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.95rem' }}>How your operator behaves</h4>
        <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
          These are not settings. The operator cannot be configured to lie, invent a number, or pretend to be a person.
        </p>
        <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
          {hardRules.map((rule) => (
            <li key={rule.id} data-testid={`kb-assistant-hard-rule-${rule.id}`} style={{ marginBottom: '0.45rem', fontSize: '0.9rem', lineHeight: 1.45 }}>
              {rule.statement}
              <span className="fb-muted"> — {rule.why}</span>
            </li>
          ))}
        </ul>
      </section>

      <section data-testid="kb-assistant-defaults" style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.95rem' }}>Defaults we start you on</h4>
        <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
          The safe path is the path of least effort. Changing these later is a later step — each one carries a reason, not a silent toggle.
        </p>
        <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
          {OVERRIDABLE_SETTINGS.map((setting) => (
            <li key={setting.id} data-testid={`kb-assistant-default-${setting.id}`} style={{ marginBottom: '0.45rem', fontSize: '0.9rem', lineHeight: 1.45 }}>
              <strong>{setting.label}.</strong>{' '}
              <span className="fb-muted">{setting.reason}</span>
            </li>
          ))}
        </ul>
      </section>

      <section data-testid="kb-assistant-coverage" style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>What we found</h4>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {COVERAGE_TOPICS.map((topic) => {
            const row = coverage.coverage[topic];
            const present = row.status === 'present';
            const snippet = row.evidence[0]?.snippet || '';
            return (
              <li
                key={topic}
                data-testid={`kb-assistant-topic-${topic}`}
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                  padding: '0.55rem 0',
                  borderBottom: '1px solid var(--line, #e5e7eb)',
                  fontSize: '0.9rem',
                  lineHeight: 1.45,
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    fontWeight: 700,
                    color: present ? '#15803d' : '#b45309',
                    minWidth: '4.5rem',
                  }}
                >
                  {present ? 'Found' : 'Missing'}
                </span>
                <span>
                  <strong>{TOPIC_COPY[topic][present ? 'found' : 'missing']}</strong>
                  {snippet ? <span className="fb-muted"> — {snippet}</span> : null}
                </span>
              </li>
            );
          })}
        </ul>
        {pagesToAdd.length > 0 && (
          <div className="fb-callout info" data-testid="kb-assistant-missing-page" style={{ marginTop: '1rem' }}>
            {pagesToAdd.map((page) => (
              <p key={page.url} style={{ margin: '0 0 0.4rem' }}>
                {page.reason} We can add it to the knowledge base if you want.
              </p>
            ))}
          </div>
        )}
        {scrapeListGaps.length > 0 && (
          <div className="fb-callout info" data-testid="kb-assistant-scrape-list" style={{ marginTop: '1rem' }}>
            {scrapeListGaps.map((page) => (
              <p key={page.url} style={{ margin: '0 0 0.4rem' }}>
                Found {page.url} on the site, but it is not in the scrape list. Pricing is already in
                the knowledge base, so we are not asking to add the page. Add this URL under Pages to
                scrape so a future re-scrape refreshes it.
              </p>
            ))}
          </div>
        )}
      </section>

      {pickerFirst ? null : (
      <section data-testid="kb-assistant-questions">
        <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.95rem' }}>
          {total === 1 ? '1 question the operator still needs' : `${total} questions the operator still needs`}
        </h4>
        <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
          Answer what you can. Every answer makes the operator better, and you can come back.
          {answeredCount > 0 ? ` ${answeredCount} of ${total} answered.` : ''}
        </p>
        {total === 0 || !current ? (
          <p className="fb-muted">The knowledge base already covers the usual gaps.</p>
        ) : (
          <>
            <p
              data-testid="kb-assistant-progress"
              style={{ fontSize: '0.85rem', fontWeight: 600, margin: '0 0 0.85rem' }}
            >
              {currentIndex + 1} of {total}
            </p>
            <div data-testid={`kb-assistant-step-${current.id}`}>
              <label
                className="fb-label"
                htmlFor={
                  current.id === CONVERSATION_GOAL_ID
                    ? 'kb-assistant-goal-select'
                    : `kb-assistant-q-${current.id}`
                }
              >
                {currentIndex + 1}. {current.question}
              </label>
              <WhyToggle key={current.id} questionId={current.id} why={current.why} />
              {current.id === CONVERSATION_GOAL_ID ? (
                renderGoalFields()
              ) : current.id === REGULATED_VERTICAL_CONFIRM_ID ? (
                <select
                  id={`kb-assistant-q-${current.id}`}
                  data-testid={`kb-assistant-answer-${current.id}`}
                  className="fb-select"
                  value={answers[current.id] || ''}
                  onChange={(e) => setAnswer(current.id, e.target.value)}
                >
                  <option value="">Choose one</option>
                  {REGULATED_VERTICAL_CONFIRM_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <textarea
                  id={`kb-assistant-q-${current.id}`}
                  data-testid={`kb-assistant-answer-${current.id}`}
                  className="fb-textarea"
                  rows={current.answerInput === 'multiline' ? 8 : 3}
                  value={answers[current.id] || ''}
                  onChange={(e) => setAnswer(current.id, e.target.value)}
                />
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem', alignItems: 'center' }}>
              <button
                type="button"
                className="fb-btn-ghost-sm"
                data-testid="kb-assistant-prev"
                disabled={currentIndex === 0}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="fb-btn-ghost-sm"
                data-testid="kb-assistant-next"
                disabled={currentIndex >= total - 1}
                onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
              >
                Next
              </button>
              {canDraft ? (
                <button
                  type="button"
                  className="fb-btn-ghost-sm"
                  data-testid="kb-assistant-done-for-now"
                  onClick={() => document.getElementById('kb-assistant-draft')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  I&apos;m done for now
                </button>
              ) : null}
            </div>
          </>
        )}
      </section>
      )}

      {canDraft && !pickerFirst ? (
        <section id="kb-assistant-draft" data-testid="kb-assistant-draft" style={{ marginTop: '1.75rem' }}>
          <h4 style={{ margin: '0 0 0.4rem', fontSize: '0.95rem' }}>Draft additions</h4>
          <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
            Draft whenever you are ready — you do not need to finish the list. Nothing is written
            until you approve. If a fact was not supplied, the draft must say unknown.
          </p>
          <button
            type="button"
            className="inbox-pill-btn-primary"
            data-testid="kb-assistant-draft-btn"
            onClick={handleDraft}
            disabled={drafting}
            style={{ opacity: drafting ? 0.6 : 1 }}
          >
            {drafting ? 'Drafting…' : 'Draft additions'}
          </button>
          {draftError ? (
            <p data-testid="kb-assistant-draft-error" style={{ color: '#b45309', marginTop: '0.75rem' }}>
              {draftError}
            </p>
          ) : null}

          {reviewOpen ? (
            <div data-testid="kb-assistant-draft-review" style={{ marginTop: '1.15rem' }}>
              <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
                These lines will be appended. Edit them if the wording is wrong. Reject discards the
                draft. The current knowledge base stays as it is until you approve.
              </p>
              <pre
                data-testid="kb-assistant-draft-diff"
                style={{
                  fontSize: '0.8rem',
                  lineHeight: 1.45,
                  padding: '0.75rem',
                  background: '#f3f4f6',
                  overflow: 'auto',
                  margin: '0 0 0.75rem',
                }}
              >
                {diffLines.join('\n')}
              </pre>
              <label className="fb-label" htmlFor="kb-assistant-draft-edit">
                Edit before approving
              </label>
              <textarea
                id="kb-assistant-draft-edit"
                data-testid="kb-assistant-draft-edit"
                className="fb-textarea"
                rows={8}
                value={draftAdditions}
                onChange={(e) => setDraftAdditions(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.85rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  data-testid="kb-assistant-approve"
                  onClick={handleApprove}
                >
                  Approve and add
                </button>
                <button
                  type="button"
                  className="fb-btn-ghost-sm"
                  data-testid="kb-assistant-reject"
                  onClick={handleReject}
                >
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          {draftApplied ? (
            <div data-testid="kb-assistant-draft-applied" style={{ marginTop: '1rem' }}>
              <p style={{ fontSize: '0.9rem', margin: '0 0 0.75rem' }}>
                Added. The previous knowledge base is still recoverable below. Test the operator with
                a question the KB could not answer before.
              </p>
              {testQuestion ? (
                <p className="fb-muted" data-testid="kb-assistant-handoff" style={{ fontSize: '0.85rem' }}>
                  Test question: {testQuestion}
                </p>
              ) : null}
              {previousContent ? (
                <button
                  type="button"
                  className="fb-btn-ghost-sm"
                  data-testid="kb-assistant-restore"
                  onClick={handleRestore}
                  style={{ marginTop: '0.75rem' }}
                >
                  Restore previous knowledge base
                </button>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
